/*!

=========================================================
* Vision UI Free React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-react/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import { Card, LinearProgress, Stack, Tooltip, Popover, IconButton, Modal, Select, FormControl, InputLabel, TextField, Box } from "@mui/material";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiProgress from "components/VuiProgress";
import VuiMenuItem from "components/VuiMenuItem";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import linearGradient from "assets/theme/functions/linearGradient";

// Vision UI Dashboard React base styles
import typography from "assets/theme/base/typography";
import colors from "assets/theme/base/colors";

// Dashboard layout components
// import WelcomeMark from "layouts/dashboard/components/WelcomeMark";
import OrderOverview from "layouts/dashboard/components/OrderOverview";
import SatisfactionRate from "layouts/dashboard/components/SatisfactionRate";

// React icons
import { FiTrendingUp, FiUsers, FiUserPlus, FiCreditCard } from "react-icons/fi";

// Data
import LineChart from "examples/Charts/LineCharts/LineChart";
import BarChart from "examples/Charts/BarCharts/BarChart";
import { lineChartDataDashboard } from "layouts/dashboard/data/lineChartData";
import { lineChartOptionsDashboard } from "layouts/dashboard/data/lineChartOptions";
import { barChartDataDashboard } from "layouts/dashboard/data/barChartData";
import { barChartOptionsDashboard } from "layouts/dashboard/data/barChartOptions";

import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import { useFontFamily } from 'utils/fontUtils';

function Dashboard() {
  const { t, i18n } = useTranslation();
  const { fontFamily } = useFontFamily();
  const { gradients } = colors;
  const { cardContent } = gradients;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const history = useHistory();
  const [period, setPeriod] = useState('last_7_days');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          history.push("/authentication/sign-in");
          return;
        }
        let params = {};
        if (period === 'custom') {
          params = { from_date: fromDate, to_date: toDate, period };
        } else {
          params = { period };
        }
        const response = await axios.get("https://api.csc-taxi.tech/api/admin/journey/stats", {
          params,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Accept-Language': 'ar'
          }
        });
        if (response.data.status === 1) {
          setStats(response.data.data);
        } else {
          setError("Failed to fetch stats");
        }
      } catch (err) {
        if (err.response?.status === 403) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          history.push("/authentication/sign-in");
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };
    // Only fetch if not custom or if custom and both dates are set
    if (period !== 'custom' || (period === 'custom' && fromDate && toDate)) {
      setLoading(true);
      fetchStats();
    }
  }, [history, period, fromDate, toDate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPeriodText = (period) => {
    switch(period) {
      case 'last_week': return t('dashboardPage.periods.lastWeek');
      case 'last_month': return t('dashboardPage.periods.lastMonth');
      case 'last_year': return t('dashboardPage.periods.lastYear');
      case 'last_7_days': return t('dashboardPage.periods.last7Days');
      case 'last_30_days': return t('dashboardPage.periods.last30Days');
      case 'custom': return t('dashboardPage.periods.custom');
      default: return t('dashboardPage.periods.previous');
    }
  };

  const prepareChartData = (values) => {
    if (!values) return { series: [], options: {} };
    
    // Sort dates chronologically
    const sortedDates = Object.keys(values).sort();
    const categories = sortedDates.map(date => {
      const [year, month, day] = date.split('-');
      return `${month}/${day}`;
    });
    const data = sortedDates.map(date => values[date]);

    return {
      series: [{
        name: 'Journeys',
        data: data
      }],
      options: {
        chart: {
          type: 'area',
          height: 310,
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth',
          width: 2,
          colors: ['#fff']
        },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.2,
            stops: [0, 90, 100]
          },
          colors: ['rgba(255, 255, 255, 0.1)']
        },
        grid: {
          borderColor: 'rgba(255, 255, 255, 0.1)',
          strokeDashArray: 2,
          xaxis: {
            lines: {
              show: false
            }
          },
          yaxis: {
            lines: {
              show: true
            }
          }
        },
        xaxis: {
          categories: categories,
          labels: {
            style: {
              colors: '#fff',
              fontSize: '12px'
            },
            rotate: -45,
            rotateAlways: true
          },
          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: '#fff',
              fontSize: '12px'
            }
          }
        },
        tooltip: {
          theme: 'dark',
          x: {
            format: 'MM/dd'
          }
        },
        markers: {
          size: 4,
          colors: ['#fff'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: {
            size: 6
          }
        }
      }
    };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3}>
          <LinearProgress />
        </VuiBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3}>
          <VuiTypography variant="h6" color="error">
            {t('common.error')}: {error}
          </VuiTypography>
        </VuiBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Period Filter */}
        <VuiBox
          mb={3}
          display="flex"
          alignItems="center"
          gap={2}
          sx={{
            background: 'rgba(25,26,58,0.85)',
            borderRadius: '16px',
            boxShadow: '0 4px 24px 0 rgba(0,0,0,0.10)',
            px: 3,
            py: 2,
            width: 'fit-content',
            minWidth: 340,
          }}
        >
          <VuiTypography
            variant="button"
            color="white"
            fontWeight="bold"
            sx={{ mr: 2, fontSize: 16 }}
          >
            {t('common.period')}:
          </VuiTypography>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 160 }}>
            <Select
              labelId="period-label"
              value={period}
              onChange={e => setPeriod(e.target.value)}
              sx={{
                background: '#191A3A',
                color: '#fff',
                borderRadius: 2,
                fontWeight: 'bold',
                fontFamily,
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
                '.MuiSelect-select': {
                  fontFamily,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: { background: '#191A3A', color: '#fff' }
                }
              }}
            >
              <VuiMenuItem value="last_week">{t('dashboardPage.periods.lastWeek')}</VuiMenuItem>
              <VuiMenuItem value="last_month">{t('dashboardPage.periods.lastMonth')}</VuiMenuItem>
              <VuiMenuItem value="last_year">{t('dashboardPage.periods.lastYear')}</VuiMenuItem>
              <VuiMenuItem value="last_7_days">{t('dashboardPage.periods.last7Days')}</VuiMenuItem>
              <VuiMenuItem value="last_30_days">{t('dashboardPage.periods.last30Days')}</VuiMenuItem>
              <VuiMenuItem value="custom">{t('dashboardPage.periods.custom')}</VuiMenuItem>
            </Select>
          </FormControl>
          {period === 'custom' && (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <VuiBox display="flex" alignItems="center" gap={2} flexWrap="wrap" sx={{ width: '100%' }}>
                <Box display="flex" alignItems="center" gap={1} sx={{ flex: '1 1 180px', minWidth: 0 }}>
                  <VuiTypography
                    variant="button"
                    color="white"
                    fontWeight="bold"
                    sx={{ fontSize: 16 }}
                  >
                    {t('from_date')}:
                  </VuiTypography>
                  <DatePicker
                    value={fromDate ? new Date(fromDate) : null}
                    onChange={date => setFromDate(date ? date.toISOString().slice(0, 10) : '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        InputLabelProps={{ shrink: true, style: { display: 'none' } }}
                        InputProps={{
                          ...params.InputProps,
                          style: {
                            color: '#191A3A',
                            background: 'transparent',
                            borderRadius: 8,
                            border: '1px solid #23244a',
                            paddingRight: 0,
                            cursor: 'pointer',
                            ...params.InputProps?.style,
                          },
                          endAdornment: params.InputProps?.endAdornment,
                        }}
                        sx={{
                          width: '100%',
                          cursor: 'pointer',
                          '& .MuiOutlinedInput-root': {
                            cursor: 'pointer',
                            '& fieldset': {
                              border: '1px solid #23244a',
                            },
                            '& input': {
                              color: '#191A3A',
                              background: 'transparent',
                              cursor: 'pointer',
                              '::placeholder': {
                                color: '#b0b3c6',
                                opacity: 1,
                              },
                            },
                            '& .MuiSvgIcon-root': {
                              color: '#191A3A',
                              marginRight: 0,
                              cursor: 'pointer',
                            },
                          },
                        }}
                        placeholder="dd/mm/yyyy"
                      />
                    )}
                    format="dd/MM/yyyy"
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={1} sx={{ flex: '1 1 180px', minWidth: 0 }}>
                  <VuiTypography
                    variant="button"
                    color="white"
                    fontWeight="bold"
                    sx={{ fontSize: 16 }}
                  >
                    {t('to_date')}:
                  </VuiTypography>
                  <DatePicker
                    value={toDate ? new Date(toDate) : null}
                    onChange={date => setToDate(date ? date.toISOString().slice(0, 10) : '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        InputLabelProps={{ shrink: true, style: { display: 'none' } }}
                        InputProps={{
                          ...params.InputProps,
                          style: {
                            color: '#191A3A',
                            background: 'transparent',
                            borderRadius: 8,
                            border: '1px solid #23244a',
                            paddingRight: 0,
                            cursor: 'pointer',
                            ...params.InputProps?.style,
                          },
                          endAdornment: params.InputProps?.endAdornment,
                        }}
                        sx={{
                          width: '100%',
                          cursor: 'pointer',
                          '& .MuiOutlinedInput-root': {
                            cursor: 'pointer',
                            '& fieldset': {
                              border: '1px solid #23244a',
                            },
                            '& input': {
                              color: '#191A3A',
                              background: 'transparent',
                              cursor: 'pointer',
                              '::placeholder': {
                                color: '#b0b3c6',
                                opacity: 1,
                              },
                            },
                            '& .MuiSvgIcon-root': {
                              color: '#191A3A',
                              marginRight: 0,
                              cursor: 'pointer',
                            },
                          },
                        }}
                        placeholder="dd/mm/yyyy"
                      />
                    )}
                    format="dd/MM/yyyy"
                  />
                </Box>
              </VuiBox>
            </LocalizationProvider>
          )}
        </VuiBox>
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={3}>
              <Card>
                <VuiBox p={2}>
                  <MiniStatisticsCard
                    title={{ text: t('income'), fontWeight: "regular" }}
                    count={formatCurrency(stats?.income?.sum || 0)}
                    percentage={{ color: "success", text: stats?.income?.change_percent || "0%" }}
                    icon={{ color: "info", component: <FiTrendingUp size="22px" color="white" /> }}
                  />
                </VuiBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <Card>
                <VuiBox p={2}>
                  <MiniStatisticsCard
                    title={{ text: t('customers') }}
                    count={stats?.customers_count?.sum || 0}
                    percentage={{ color: "success", text: stats?.customers_count?.change_percent || "0%" }}
                    icon={{ color: "info", component: <FiUsers size="22px" color="white" /> }}
                  />
                </VuiBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <Card>
                <VuiBox p={2}>
                  <MiniStatisticsCard
                    title={{ text: t('new_clients') }}
                    count={stats?.new_clients_count?.sum || 0}
                    percentage={{ color: "success", text: stats?.new_clients_count?.change_percent || "0%" }}
                    icon={{ color: "info", component: <FiUserPlus size="22px" color="white" /> }}
                  />
                </VuiBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <Card>
                <VuiBox p={2}>
                  <MiniStatisticsCard
                    title={{ text: t('expenses') }}
                    count={formatCurrency(stats?.expenses?.sum || 0)}
                    percentage={{ color: "success", text: stats?.expenses?.change_percent || "0%" }}
                    icon={{ color: "info", component: <FiCreditCard size="22px" color="white" /> }}
                  />
                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6} xl={7}>
              <Card>
                <VuiBox sx={{ height: "100%" }}>
                  <VuiTypography variant="lg" color="white" fontWeight="bold" mb="5px">
                  {t('common.journey_overview')}
                  </VuiTypography>
                  <VuiBox display="flex" alignItems="center" mb="40px">
                    <VuiTypography variant="button" color="success" fontWeight="bold">
                      {stats?.journeys_count?.change_percent || "0%"} {" "}
                      <VuiTypography variant="button" color="text" fontWeight="regular">
                      {t('common.than')} {formatPeriodText(period)}
                      </VuiTypography>
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox sx={{ height: "310px" }}>
                    <LineChart
                      lineChartData={prepareChartData(stats?.journeys_count?.values).series}
                      lineChartOptions={prepareChartData(stats?.journeys_count?.values).options}
                    />
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;

